import { useContext, useEffect, useState } from 'react';

import { Modal, Progress, Snackbar } from '@atom/mui';
import { MediaFolderTree } from '@atom/types/media';

import MoveFolderTree from './moveFolderTree/MoveFolderTree';
import FilesTabContext from './FilesTabContext';

import classes from './filesTab.module.css';

type MoveMediaFolderModalProps = {
  type: 'media' | 'folder';
  ids: string[];
  open: boolean;
  closeModal: () => void;
  disabled?: string[];
};

const styles = {
  content: { padding: 0 },
  progress: { height: '100%' },
};

const MoveMediaFolderModal = ({
  type,
  ids,
  open,
  closeModal,
  disabled,
}: MoveMediaFolderModalProps) => {
  const { moveFolder, moveMedia, onMoveComplete } = useContext(FilesTabContext);

  const [selected, setSelected] = useState<MediaFolderTree | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loadingMove, setLoadingMove] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setIsEditing(false);
      setLoadingMove(false);
    }
  }, [open]);

  const isFolderMove = type === 'folder';
  const movingFolderId = isFolderMove ? (ids[0] ?? '') : '';
  const disabledIds = new Set(disabled ?? []);

  const handleMove = async () => {
    if (!selected) {
      return;
    }

    setLoadingMove(true);

    try {
      if (type === 'media') {
        await moveMedia(ids, selected.id);
      } else if (ids[0]) {
        await moveFolder(ids[0], selected.id);
      }

      closeModal();
      Snackbar.info({
        message: `Moved ${ids.length === 1 ? 'item' : 'items'} to ${selected.name}.`,
      });
      onMoveComplete(ids[0] ?? '', selected.id);
    } finally {
      setLoadingMove(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Move to Folder"
      confirmButtonText="Move"
      onConfirm={handleMove}
      onCancel={closeModal}
      contentStyle={styles.content}
      disabled={!selected || isEditing || loadingMove}
      CancelButtonProps={{ 'data-cy': 'filesTabMoveMediaCancel' }}
      ConfirmButtonProps={{ 'data-cy': 'filesTabMoveMediaConfirm' }}
    >
      <div className={classes.modalTreeContainer}>
        {loadingMove ? (
          <Progress style={styles.progress} />
        ) : (
          <MoveFolderTree
            open={open}
            isFolderMove={isFolderMove}
            movingFolderId={movingFolderId}
            disabledIds={disabledIds}
            selectedId={selected?.id ?? ''}
            onSelectionChange={setSelected}
            onEditingChange={setIsEditing}
          />
        )}
      </div>
    </Modal>
  );
};

export default MoveMediaFolderModal;
